import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { isSafeChatAppPath } from 'lib-trend-core/chat';
import { ChatAppSessionService } from './chat-app-session.service';

/**
 * A chat app session is required. Without one the user signs in and comes back to the
 * address they asked for (RF-04) — only when that address is a chat app path.
 */
export const chatAppSessionGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree => {
  const session = inject(ChatAppSessionService);
  if (session.isAuthenticated()) {
    return true;
  }
  const returnUrl = isSafeChatAppPath(state.url) && state.url !== '/' ? state.url : undefined;
  return inject(Router).createUrlTree(['/login'], { queryParams: returnUrl ? { returnUrl } : {} });
};

/**
 * The company has the internal chat module and the user holds a role that may use the
 * product (RF-03). The server refuses independently; this only lands the user on the
 * explanation instead of an empty chat.
 */
export const chatAppAccessGuard: CanActivateFn = (): boolean | UrlTree => {
  const session = inject(ChatAppSessionService);
  const router = inject(Router);
  if (!session.hasAllowedRole()) {
    return router.createUrlTree(['/unavailable'], { queryParams: { reason: 'role' } });
  }
  if (!session.hasChatModule()) {
    return router.createUrlTree(['/unavailable'], { queryParams: { reason: 'module' } });
  }
  return true;
};

/** Signed-in users skip the sign-in page. */
export const chatAppGuestGuard: CanActivateFn = (route: ActivatedRouteSnapshot): boolean | UrlTree => {
  const session = inject(ChatAppSessionService);
  if (!session.isAuthenticated()) {
    return true;
  }
  const returnUrl = route.queryParamMap.get('returnUrl');
  return inject(Router).parseUrl(isSafeChatAppPath(returnUrl) ? returnUrl : '/');
};
